<template>
  <div class="pvtprediction-dashboard" :class="{ 'dark-mode': isDarkMode }">
    <EmbeddedBreadcrumbBar
      :items="breadcrumbItems"
      home-mode="pvt"
      @pvt-navigate="navigatePvtPath"
    />
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <iframe
      v-else
      :src="iframeSrc"
      class="prediction-iframe"
      ref="predictionIframe"
      @load="handleIframeLoad"
    ></iframe>
  </div>
  <div v-if="isLoading" class="loader-container-eda">
    <div class="loader">
      <div class="spinner"></div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import EmbeddedBreadcrumbBar from "@/components/layout/Common/EmbeddedBreadcrumbBar.vue";
import {
  notifyIframeDarkMode,
  notifyIframeTenant,
  notifyIframePvtRoute,
  getIframeOrigin,
  PVT_ROUTE_CHANGED_MESSAGE,
} from "@/utils/iframeDarkMode";
import {
  buildPvtBreadcrumbItems,
  normalizePvtIframePath,
} from "@/utils/pvtIframeRoute";

export default {
  name: "PVTPrediction",
  components: {
    EmbeddedBreadcrumbBar,
  },
  data() {
    return {
      error: null,
      isLoading: true,
      iframeSrc: null,
      pvtIframePath: "/",
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    breadcrumbItems() {
      return buildPvtBreadcrumbItems(this.pvtIframePath);
    },
    pvtOrigin() {
      return process.env.VUE_APP_PVT_PREDICTION_URL;
    },
    routeTenantId() {
      return this.$route.path.split("/")[1] || "";
    },
  },
  mounted() {
    window.addEventListener("message", this.handleIframeMessage);
    this.refreshIframeSrc();
    this.isLoading = true;
    setTimeout(() => {
      this.isLoading = false;
    }, 3000);
  },
  methods: {
    buildIframeUrl() {
      const token = localStorage.getItem("authToken");
      const tenantId = this.$route.path.split("/")[1];
      if (!token || !tenantId) {
        this.error = "Authentication required";
        this.$router.push("/login");
        return null;
      }

      const darkMode = this.isDarkMode ? "true" : "false";
      const origin = String(this.pvtOrigin || "").replace(/\/$/, "");
      const parentOrigin = encodeURIComponent(window.location.origin);
      return `${origin}/?token=${token}&tenantId=${tenantId}&darkMode=${darkMode}&parentOrigin=${parentOrigin}`;
    },
    refreshIframeSrc() {
      const url = this.buildIframeUrl();
      if (url) {
        this.iframeSrc = url;
      }
    },
    syncIframeDarkMode() {
      this.$nextTick(() => {
        notifyIframeDarkMode(this.$refs.predictionIframe, this.isDarkMode, this.pvtOrigin);
      });
    },
    syncIframeTenant(tenantId) {
      this.$nextTick(() => {
        notifyIframeTenant(
          this.$refs.predictionIframe,
          tenantId || this.routeTenantId,
          this.pvtOrigin
        );
      });
    },
    handleIframeLoad() {
      const iframe = this.$refs.predictionIframe;
      if (iframe) {
        this.error = null;
        notifyIframeDarkMode(iframe, this.isDarkMode, this.pvtOrigin);
        notifyIframeTenant(iframe, this.routeTenantId, this.pvtOrigin);
        setTimeout(() => {
          this.isLoading = false;
        }, 3000);
      }
    },
    handleIframeMessage(event) {
      if (event.origin !== getIframeOrigin(this.pvtOrigin)) {
        return;
      }
      const data = event.data;
      if (!data) {
        return;
      }
      if (data.type === PVT_ROUTE_CHANGED_MESSAGE) {
        this.pvtIframePath = normalizePvtIframePath(data.pathname);
        return;
      }
      if (data.type === "TOKEN_EXPIRED") {
        this.handleTokenExpiration();
      }
    },
    navigatePvtPath(pathname) {
      const path = normalizePvtIframePath(pathname);
      this.pvtIframePath = path;
      notifyIframePvtRoute(this.$refs.predictionIframe, path, this.pvtOrigin);
    },
    handleTokenExpiration() {
      this.$store.dispatch("tenantId/logoutUser");
      this.error = "Your session has expired. Please login again.";
    },
  },
  watch: {
    isDarkMode() {
      this.syncIframeDarkMode();
    },
    routeTenantId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.syncIframeTenant(newId);
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("message", this.handleIframeMessage);
  },
};
</script>

<style scoped>
.pvtprediction-dashboard {
  width: 100%;
  height: calc(100vh - 64px);
  position: relative;
  display: flex;
  flex-direction: column;
}

.prediction-iframe {
  width: 100%;
  flex: 1;
  min-height: 0;
  border: none;
}

.loader-container-eda {
  position: absolute;
  top: 32px;
  left: 0;
  width: 100%;
  height: calc(100% - 32px);
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8f9fa;
  z-index: 10;
}

.loader {
  text-align: center;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 4px solid #145B80;
  border-top: 4px solid #f8f9fa;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin: 0 auto 16px;
}

.dark-mode .spinner {
  border-color: #145B80;
  border-top: 4px solid #1F2937 !important;
}

.error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  padding: 20px;
  color: #dc3545;
  background: #f8f9fa;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
